import { Grid, IconButton, Paper } from '@mui/material';
import {
  Condition,
  ConditionGroup,
  ConditionOperation,
  FieldTemplate,
  FieldType,
  ReferenceType,
} from 'api/models/FieldTemplate';
import ConditionFieldRenderer from './ConditionFieldRenderer';
import { Close } from '@mui/icons-material';
import ConditionOperationSelect from './ConditionOperationSelect';
import ConditionFieldAutocomplete from './ConditionFieldAutocomplete';
import { FormTemplateType } from 'api/models';
import { produce } from 'immer';
import { set, get } from 'object-path';
import { getConditionValueType, getOperatorByType, isUUID } from 'utils';

interface ConditionGroupRowProps {
  condition: Condition;
  rootGroup: ConditionGroup;
  onChange: (condition: ConditionGroup | undefined) => void;
  patientFields?: FieldTemplate[];
  fieldTemplates?: FieldTemplate[];
  formTemplateTypes?: FormTemplateType[];
  path: string;
  disabled?: boolean;
}

export default function ConditionRow({
  condition,
  onChange,
  patientFields = [],
  fieldTemplates = [],
  formTemplateTypes = [],
  path,
  rootGroup,
  disabled,
}: ConditionGroupRowProps) {
  const onFieldChange = (field: FieldTemplate | null) => {
    if (field === null) {
      const newRootCondition = produce(rootGroup, draft => {
        set(draft, `${path}.reference`, {
          alias: undefined,
          attribute: undefined,
          type: undefined,
          valueType: undefined,
        });
        set(draft, `${path}.value`, null);
      });
      return onChange(newRootCondition);
    }

    const valueType = getConditionValueType(field);
    const operations = getOperatorByType(valueType);
    // if it's uuid it means that it's not a patient field
    if (isUUID(field.id)) {
      const newRootCondition = produce(rootGroup, draft => {
        set(draft, `${path}.reference`, {
          alias: field.alias,
          attribute: '',
          type: ReferenceType.formFieldReference,
          valueType,
          value: field.fieldType === FieldType.Select ? [] : undefined,
        });
        set(draft, `${path}.value`, undefined);
        set(draft, `${path}.operation`, operations[0]);
      });
      return onChange(newRootCondition);
    } else {
      const newRootCondition = produce(rootGroup, draft => {
        set(draft, `${path}.reference`, {
          alias: undefined,
          attribute: field.id,
          type: ReferenceType.patientFieldReference,
          valueType,
          value: field.fieldType === FieldType.Select ? [] : undefined,
        });
        set(draft, `${path}.value`, undefined);
        set(draft, `${path}.operation`, operations[0]);
      });
      return onChange(newRootCondition);
    }
  };

  const onOperationChange = (operation: ConditionOperation) => {
    const newRootCondition = produce(rootGroup, draft => {
      set(draft, `${path}.operation`, operation);
    });
    onChange(newRootCondition);
  };

  const onValueChange = (value?: string | string[] | number | null) => {
    const newRootCondition = produce(rootGroup, draft => {
      set(draft, `${path}.value`, value);
    });
    onChange(newRootCondition);
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 1,
        width: '100%',
      }}
    >
      <Grid container gap={1} wrap="nowrap">
        <Grid item xs={5}>
          <ConditionFieldAutocomplete
            patientFields={patientFields}
            fieldTemplates={fieldTemplates}
            formTemplateTypes={formTemplateTypes}
            condition={condition}
            onChange={onFieldChange}
            disabled={disabled}
            showMainCategories
          />
        </Grid>
        <Grid item xs={2}>
          <ConditionOperationSelect
            operation={condition?.operation}
            onChange={onOperationChange}
            type={condition?.reference.valueType}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={4}>
          <ConditionFieldRenderer
            patientFieldAttribute={condition?.reference.attribute}
            alias={condition?.reference.alias}
            value={condition?.value}
            onChange={onValueChange}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            disabled={disabled}
            onClick={() => {
              // TODO: update this once we support nested conditions
              const parentIndex = path.split('.')[1];
              const newRootCondition = produce(rootGroup, draft => {
                get(draft, 'conditions').splice(parentIndex, 1);
              });

              // If newRootCondition is undefined, that means we removed the whole group
              // Because the initial value is undefined, if don't do this the automatically added group will remain with empty conditions array
              onChange(newRootCondition.conditions.length === 0 ? undefined : newRootCondition);
            }}
          >
            <Close />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
}
